import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { motion } from 'framer-motion';
import { Chart } from 'chart.js/auto';
import {
  BarChart3,
  Users,
  FileCheck,
  AlertCircle,
  Search,
  RefreshCw,
  PlusCircle,
  Trash2,
  ChevronDown,
  ChevronUp,
  Filter,
  Activity,
  Clock,
  Calendar,
  CheckCircle,
  XCircle,
  Loader2,
  PieChart,
  TrendingUp
} from 'lucide-react';
import { getAdminDashboardData, deleteAdminAccount } from '../../services/operations/adminAPI';
import toast from 'react-hot-toast';

export default function AdminDashboard() {
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ 
    key: "drawingNumber", 
    direction: "ascending" 
  });
  const [filterStatus, setFilterStatus] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const taskChartRef = useRef(null);
  const teamChartRef = useRef(null);
  const performanceChartRef = useRef(null);
  const taskChartInstance = useRef(null);
  const teamChartInstance = useRef(null);
  const performanceChartInstance = useRef(null);

  const fetchDashboardData = async () => {
    setIsRefreshing(true);
    try {
      const response = await getAdminDashboardData();
      if (response?.success) {
        setTeams(response.data);
      } else {
        toast.error(response?.message || "Failed to fetch dashboard data");
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      toast.error("Error fetching dashboard data");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    
    const intervalId = setInterval(() => {
      fetchDashboardData();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  const handleDeleteAdmin = async () => {
    if (window.confirm("Are you sure you want to delete your admin account? This will delete all associated data.")) {
      try {
        const res = await deleteAdminAccount();
        if (res.success) {
          toast.success("Account deleted successfully");
          navigate("/login");
        } else {
          toast.error(res.message || "Error deleting account");
        }
      } catch (error) {
        console.error("Error deleting account:", error);
        toast.error("Failed to delete account");
      }
    }
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (status) => {
    setFilterStatus(status);
  };

  const filteredAndSortedTeams = useMemo(() => {
    let filteredTeams = [...teams];
    
    if (filterStatus) {
      filteredTeams = filteredTeams.filter(team => team.status === filterStatus);
    }
    
    if (searchTerm) {
      filteredTeams = filteredTeams.filter(team => 
        team.drawingNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.chairman.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.chairman.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.scribe.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.scribe.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.team.some(member => member.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (sortConfig.key) {
      filteredTeams.sort((a, b) => {
        const getNestedProperty = (obj, path) => {
          return path.split('.').reduce((prev, curr) => 
            prev ? prev[curr] : null, obj);
        };
        
        const aValue = getNestedProperty(a, sortConfig.key);
        const bValue = getNestedProperty(b, sortConfig.key);
        
        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filteredTeams;
  }, [teams, searchTerm, sortConfig, filterStatus]);

  const analytics = useMemo(() => {
    const completedTeams = teams.filter(team => team.status === "Completed").length;
    const pendingTeams = teams.filter(team => team.status === "Pending").length;
    const inProgressTeams = teams.filter(team => team.status === "In Progress").length;
    const cancelledTeams = teams.filter(team => team.status === "Cancelled").length;
    
    const totalTeamMembers = teams.reduce((acc, team) => acc + team.team.length, 0);
    const averageTeamSize = teams.length > 0 ? (totalTeamMembers / teams.length).toFixed(1) : 0;
    
    return {
      totalTeams: teams.length,
      completedTeams,
      pendingTeams,
      inProgressTeams,
      cancelledTeams,
      completionRate: teams.length > 0 ? ((completedTeams / teams.length) * 100).toFixed(0) : 0,
      totalTeamMembers,
      averageTeamSize
    };
  }, [teams]);

  const getMonthlyPerformanceData = (teams) => {
    const last6Months = Array.from({ length: 6 }, (_, i) => {
      const date = subMonths(new Date(), i);
      return {
        month: format(date, 'MMM'),
        startDate: startOfMonth(date),
        endDate: endOfMonth(date)
      };
    }).reverse();

    const monthlyData = last6Months.map(({ month, startDate, endDate }) => {
      const monthTeams = teams.filter(team => {
        const teamDate = team.lastUpdated ? new Date(team.lastUpdated) : new Date();
        return teamDate >= startDate && teamDate <= endDate;
      });

      return {
        month,
        completedTasks: monthTeams.filter(team => team.status === 'Completed').length,
        newTasks: monthTeams.length
      };
    });

    return {
      months: monthlyData.map(data => data.month),
      completedTasks: monthlyData.map(data => data.completedTasks),
      newTasks: monthlyData.map(data => data.newTasks)
    };
  };

  useEffect(() => {
    if (!teams.length) return;

    // Task Status Chart
    const taskCtx = taskChartRef.current?.getContext('2d');
    if (taskCtx) {
      if (taskChartInstance.current) {
        taskChartInstance.current.destroy();
      }

      taskChartInstance.current = new Chart(taskCtx, {
        type: 'pie',
        data: {
          labels: ['Completed', 'Pending'],
          datasets: [{
            data: [analytics.completedTeams, analytics.pendingTeams],
            backgroundColor: ['#10B981', '#F59E0B', '#3B82F6', '#EF4444']
          }]
        },
        options: {
          responsive: true,
          plugins: {
            legend: {
              position: 'bottom'
            }
          }
        }
      });
    }

    // Team Members Chart
    const teamCtx = teamChartRef.current?.getContext('2d');
    if (teamCtx) {
      if (teamChartInstance.current) {
        teamChartInstance.current.destroy();
      }

      const displayTeams = teams.slice(0, 5);
      teamChartInstance.current = new Chart(teamCtx, {
        type: 'bar',
        data: {
          labels: displayTeams.map(team => `Drawing ${team.drawingNumber}`),
          datasets: [{
            label: 'Team Members',
            data: displayTeams.map(team => team.team.length),
            backgroundColor: '#3B82F6'
          }]
        },
        options: {
          responsive: true,
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1
              }
            }
          }
        }
      });
    }

    // Performance Chart with real data
    const perfCtx = performanceChartRef.current?.getContext('2d');
    if (perfCtx) {
      if (performanceChartInstance.current) {
        performanceChartInstance.current.destroy();
      }

      const performanceData = getMonthlyPerformanceData(teams);
      
      performanceChartInstance.current = new Chart(perfCtx, {
        type: 'line',
        data: {
          labels: performanceData.months,
          datasets: [
            {
              label: 'Completed Tasks',
              data: performanceData.completedTasks,
              borderColor: '#10B981',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              tension: 0.3,
              fill: true
            },
            {
              label: 'New Tasks',
              data: performanceData.newTasks,
              borderColor: '#3B82F6',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              tension: 0.3,
              fill: true
            }
          ]
        },
        options: {
          responsive: true,
          plugins: {
            legend: {
              position: 'top'
            },
            tooltip: {
              mode: 'index',
              intersect: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                precision: 0
              }
            }
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
          }
        }
      });
    }

    return () => {
      taskChartInstance.current?.destroy();
      teamChartInstance.current?.destroy();
      performanceChartInstance.current?.destroy();
    };
  }, [teams, analytics]);
   
   if (isLoading) {
      return (
        <div className="min-h-screen bg-gradient-to-br  flex items-center justify-center relative overflow-hidden">
          {/* Animated background */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center relative z-10"
          >
            <div className="relative mb-8">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-20 h-20 mx-auto"
              >
                <div className="w-full h-full border-4 border-blue-500/30 border-t-blue-500 rounded-full"></div>
              </motion.div>
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute inset-0 w-20 h-20 mx-auto bg-blue-500/20 rounded-full blur-xl"
              ></motion.div>
            </div>
            
            <motion.h2 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-3xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-4"
            >
              Loading Admin Dashboard
            </motion.h2>
            
            <motion.p 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-black/60 text-lg"
            >
              Preparing your analytics and insights...
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex justify-center space-x-2 mt-6"
            >
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                  className="w-2 h-2 bg-blue-400 rounded-full"
                />
              ))}
            </motion.div>
          </motion.div>
        </div>
      );
    }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 pb-10">
      {/* Modern Header with Glassmorphism */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-indigo-600/10 to-purple-600/10"></div>
        <div className="relative backdrop-blur-sm bg-white/70 border-b border-white/20 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg">
                  <BarChart3 className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                    Admin Dashboard
                  </h1>
                  <p className="text-slate-600 mt-1">Manage your HAZOP teams and projects</p>
                </div>
              </div>
              
              <div className="flex flex-wrap items-center gap-4">
                <div className="relative group">
                  <input
                    type="text"
                    placeholder="Search teams..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 pr-4 py-3 bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 text-slate-700 shadow-lg transition-all duration-300 w-full sm:w-80"
                  />
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5 group-focus-within:text-blue-500 transition-colors duration-200" />
                </div>
                
                <button
                  onClick={fetchDashboardData}
                  className="p-3 bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl text-slate-600 hover:text-slate-800 hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                  disabled={isRefreshing}
                >
                  <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
                
                <button
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 py-3 px-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 transform hover:scale-105"
                  onClick={() => navigate("/dashboard/create-team")}
                >
                  <PlusCircle className="h-5 w-5" />
                  <span className="font-medium">Start Hazop</span>
                </button>
                
                {/* <button
                  className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white py-3 px-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 transform hover:scale-105"
                  onClick={handleDeleteAdmin}
                >
                  <Trash2 className="h-5 w-5" />
                  <span className="font-medium">Delete Account</span>
                </button> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Modern Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="group relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-200 to-indigo-300 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl hover:scale-105">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Total Teams</h3>
                <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-800 mb-2">{analytics.totalTeams}</p>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-500" />
                <span className="text-sm text-green-600 font-medium">Active projects</span>
              </div>
            </div>
          </div>

          <div className="group relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-200 to-green-300 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl hover:scale-105">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Completion Rate</h3>
                <div className="p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <FileCheck className="h-6 w-6 text-white" />
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-800 mb-2">{analytics.completionRate}%</p>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-emerald-500 to-green-600 rounded-full transition-all duration-1000"
                  style={{ width: `${analytics.completionRate}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="group relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-100 to-purple-200 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl hover:scale-105">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Avg. Team Size</h3>
                <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-800 mb-2">{analytics.averageTeamSize}</p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">members per team</span>
              </div>
            </div>
          </div>

          <div className="group relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-orange-600/10 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl hover:scale-105">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Pending Tasks</h3>
                <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <AlertCircle className="h-6 w-6 text-white" />
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-800 mb-2">{analytics.pendingTeams}</p>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                <span className="text-sm text-amber-600 font-medium">Awaiting completion</span>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-600/5 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Task Status Overview</h2>
                  <p className="text-slate-600 text-sm mt-1">Distribution of project statuses</p>
                </div>
                <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                  <PieChart className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="h-64 relative">
                <canvas ref={taskChartRef} />
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-600/5 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Team Members Per Drawing</h2>
                  <p className="text-slate-600 text-sm mt-1">Team size distribution</p>
                </div>
                <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                  <BarChart3 className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="h-64 relative">
                <canvas ref={teamChartRef} />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-green-600/5 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Performance Overview</h2>
                  <p className="text-slate-600 text-sm mt-1">Monthly progress tracking</p>
                </div>
                <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg">
                  <Activity className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="h-72 relative">
                <canvas ref={performanceChartRef} />
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-orange-600/5 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Recent Activity</h2>
                  <p className="text-slate-600 text-sm mt-1">Latest team updates</p>
                </div>
                <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-lg">
                  <Clock className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="space-y-4">
                {teams.slice(0, 5).map((team, index) => (
                  <div key={index} className="flex items-start gap-4 p-3 rounded-xl bg-white/50 backdrop-blur-sm border border-white/20 hover:bg-white/70 transition-all duration-200">
                    <div className="flex-shrink-0">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center shadow-lg ${
                        team.status === 'Completed' 
                          ? 'bg-gradient-to-br from-emerald-500 to-green-600' 
                          : team.status === 'Cancelled' 
                            ? 'bg-gradient-to-br from-red-500 to-pink-600'
                            : 'bg-gradient-to-br from-blue-500 to-indigo-600'
                      }`}>
                        {team.status === 'Completed' ? (
                          <CheckCircle className="h-5 w-5 text-white" />
                        ) : team.status === 'Cancelled' ? (
                          <XCircle className="h-5 w-5 text-white" />
                        ) : (
                          <Clock className="h-5 w-5 text-white" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">
                        Team for Drawing {team.drawingNumber}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Status: <span className="font-medium">{team.status}</span>
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {team.lastUpdated || 'Recently updated'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Modern Teams Table */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-500/5 to-gray-600/5 rounded-2xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
            <div className="p-6 border-b border-white/20 bg-gradient-to-r from-slate-50/50 to-white/50">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">
                    Teams Overview
                    {isLoading && (
                      <span className="ml-3 inline-flex items-center text-sm text-slate-500">
                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        Loading...
                      </span>
                    )}
                  </h2>
                  <p className="text-slate-600 text-sm mt-1">Manage and monitor all your teams</p>
                </div>
                <div className="flex items-center gap-4">
                  <select
                    value={filterStatus || ''}
                    onChange={(e) => handleFilterChange(e.target.value || null)}
                    className="bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 shadow-lg"
                  >
                    <option value="">All Status</option>
                    <option value="Completed">Completed</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>
              </div>
            </div>
            
            {filteredAndSortedTeams.length === 0 ? (
              <div className="p-12 text-center">
                <div className="p-4 bg-slate-100 rounded-full w-fit mx-auto mb-4">
                  <Users className="h-8 w-8 text-slate-400" />
                </div>
                <p className="text-slate-500 text-lg">
                  {searchTerm ? "No teams found matching your search." : "No teams available."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-slate-50 to-white border-b border-white/20">
                    <tr>
                      <th 
                        className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-white/50 transition-colors duration-200 rounded-tl-xl"
                        onClick={() => handleSort('drawingNumber')}
                      >
                        <div className="flex items-center gap-2">
                          Drawing Number
                          {sortConfig.key === 'drawingNumber' && (
                            sortConfig.direction === 'ascending' ? 
                              <ChevronUp className="h-4 w-4 text-blue-500" /> : 
                              <ChevronDown className="h-4 w-4 text-blue-500" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Chairman
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Scribe
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Team Members
                      </th>
                      <th 
                        className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-white/50 transition-colors duration-200 rounded-tr-xl"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center gap-2">
                          Status
                          {sortConfig.key === 'status' && (
                            sortConfig.direction === 'ascending' ? 
                              <ChevronUp className="h-4 w-4 text-blue-500" /> : 
                              <ChevronDown className="h-4 w-4 text-blue-500" />
                          )}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/20">
                    {filteredAndSortedTeams.map((team, index) => (
                      <tr 
                        key={index}
                        className="hover:bg-white/50 transition-all duration-300 group"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-800">
                          <span className="px-3 py-1 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg">
                            {team.drawingNumber}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          <div className="font-medium">{team.chairman.firstName} {team.chairman.lastName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          <div className="font-medium">{team.scribe.firstName} {team.scribe.lastName}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          <div className="flex flex-wrap gap-2">
                            {team.team.map((member, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-slate-100 to-gray-100 text-slate-700 shadow-sm"
                              >
                                {member}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-lg ${
                              team.status === "Completed"
                                ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white"
                                : team.status === "Pending"
                                ? "bg-gradient-to-r from-amber-500 to-yellow-600 text-white"
                                : team.status === "In Progress"
                                ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white"
                                : "bg-gradient-to-r from-red-500 to-pink-600 text-white"
                            }`}
                          >
                            {team.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}